import React, { useState, useEffect, useRef } from "react";
import {
  Typography,
  Box,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableBody,
  TableCell,
  Paper,
  Stack,
  TextField,
  IconButton,
  CircularProgress,
  Container,
  Tabs,
  Tab,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Pagination,
} from "@mui/material";
import VisibilityIcon from "@mui/icons-material/Visibility";
import DeleteIcon from "@mui/icons-material/Delete";
import { ToastContainer, toast } from "react-toastify";
import axios from "axios";
import 'react-toastify/dist/ReactToastify.css';
import AdminDashboard from "../../Admin/Dashboard/Dashboard";
import PropertyRequestModal from "./WantedPropertyModal";
import { BASE_URL } from "../../Api/ApiUrls";

const PropertyRequests = () => {
  const [requests, setRequests] = useState([]);
  const [isLoading, setIsLoading] = useState(true); // single source of truth
  const [hasLoadedOnce, setHasLoadedOnce] = useState(false);
  const [search, setSearch] = useState("");
  const [selectedRow, setSelectedRow] = useState(null);
  const [openModal, setOpenModal] = useState(false);
  const [selectedLookingFor, setSelectedLookingFor] = useState('');
  const [selectedPropertyType, setSelectedPropertyType] = useState('');

  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [progressiveLoading, setProgressiveLoading] = useState(false);

  // Refs for debouncing and aborting
  const requestControllerRef = useRef(null);
  const searchDebounceRef = useRef(null);
  const latestRequestId = useRef(0);

  const PROPERTY_TYPE_OPTIONS = {
    Purchase: ['Plot', 'Land', 'Rental with Duplex Building', 'Duplex House', 'PG', 'Office', 'Flat', 'Villas', 'Commercial Building', 'Apartment', 'Rental Building'],
    Rent: ['House', 'Duplex House', 'Villa', 'Commercial Plot', 'Commercial Land', 'Commercial Building', 'PG', 'Office', 'Flat'],
    Lease: ['House', 'Duplex House', 'Villa', 'Plot', 'Commercial Plot', 'Commercial Land', 'Commercial Building', 'PG', 'Office', 'Flat'],
    'JV/JD': ['Land', 'Plot'],
    'Build to Suit': ['Land', 'Plot']
  };

  // Core fetch. Always resolves with { data, pagination } or null on failure.
  const fetchRequests = async (page, size, searchQuery, lookingFor, propertyType) => {
    try {
      if (requestControllerRef.current) {
        requestControllerRef.current.abort();
      }
      requestControllerRef.current = new AbortController();
      const signal = requestControllerRef.current.signal;

      // Build URL with filtering and search parameters
      const params = new URLSearchParams();
      params.append('page', page);
      params.append('page_size', size);
      
      if (lookingFor) {
        params.append('looking_for', lookingFor);
      }
      
      if (propertyType) {
        params.append('property_type', propertyType);
      }

      if (searchQuery) {
        params.append('search', searchQuery);
      }
      
      // Use appropriate endpoint
      let url;
      if (lookingFor || searchQuery) {
        url = `${BASE_URL}/property-request/type/?${params.toString()}`;
      } else {
        url = `${BASE_URL}/property-request/?${params.toString()}`;
      }
      
      const res = await axios.get(url, { signal });

      const propertiesData = res.data?.data || res.data || [];
      const pagination = res.data?.pagination || {};

      // Format data using user fields directly from serializer
      const formatted = propertiesData.map((item) => ({
        ...item,
        id: item.req_id,
        locations: item.pro_loc || [],
        location: item.locations_str || item.location || "-",
        lat: item.pro_loc?.[0]?.lat || "-",
        long: item.pro_loc?.[0]?.long || "-",
        created_at: item.created_at,
        username: item.user_username || "-",
        mobile_no: item.user_mobile_no || "-",
        user_name: item.user_first_name && item.user_last_name ? 
          `${item.user_first_name || ''} ${item.user_last_name || ''}`.trim() : "-",
        user_mobile: item.user_mobile_no || "-"
      }));

      return { data: formatted, pagination };
    } catch (error) {
      if (!axios.isCancel(error)) {
        console.error("Error fetching data:", error);
        toast.error("Failed to fetch property requests");
      }
      return null;
    }
  };

  // Apply a successful fetch result to state, in one consistent place.
  const applyResult = (result, page) => {
    if (!result) return;
    const { data, pagination } = result;
    setRequests(data);
    setCurrentPage(pagination.current_page ?? page);
    setTotalPages(pagination.total_pages ?? 1);
    setTotalCount(pagination.total_count ?? data.length);
  };

  // Unified loader for page <= 100. Always: loading on, fetch, apply, loading off.
  const loadPage = async (page, size, searchQuery, lookingFor, propertyType) => {
    const requestId = ++latestRequestId.current;
    setIsLoading(true);
    const result = await fetchRequests(page, size, searchQuery, lookingFor, propertyType);
    if (requestId !== latestRequestId.current) return; // a newer request superseded this one
    applyResult(result, page);
    setIsLoading(false);
    setHasLoadedOnce(true);
  };

  // Progressive loader for page > 100, fetched in chunks of 100.
  const loadProgressively = async (page, size, searchQuery, lookingFor, propertyType) => {
    const requestId = ++latestRequestId.current;
    setIsLoading(true);
    setProgressiveLoading(true);

    const totalChunks = Math.ceil(size / 100);
    let allProps = [];
    let lastPagination = {};

    for (let i = 0; i < totalChunks; i++) {
      const result = await fetchRequests(page, 100, searchQuery, lookingFor, propertyType);
      if (requestId !== latestRequestId.current) return; // superseded mid-flight
      if (result) {
        allProps = [...allProps, ...result.data];
        lastPagination = result.pagination;
        setRequests([...allProps]);
        if (i === 0) {
          // First chunk landed — table is no longer empty, drop the full-screen spinner.
          setIsLoading(false);
        }
      }
      if (i < totalChunks - 1) {
        await new Promise((resolve) => setTimeout(resolve, 150));
      }
    }

    if (requestId !== latestRequestId.current) return;
    const recalculatedTotalPages = Math.ceil((lastPagination.total_count || 0) / size);
    setTotalPages(recalculatedTotalPages || 1);
    setTotalCount(lastPagination.total_count || allProps.length);
    setCurrentPage(page);
    setIsLoading(false);
    setProgressiveLoading(false);
    setHasLoadedOnce(true);
  };

  const runLoad = (page, size, searchQuery, lookingFor, propertyType) => {
    if (size > 100) {
      loadProgressively(page, size, searchQuery, lookingFor, propertyType);
    } else {
      loadPage(page, size, searchQuery, lookingFor, propertyType);
    }
  };

  // Tab switch OR page size change -> reset to page 1, reload (single effect, no duplicate fetch on mount)
  useEffect(() => {
    runLoad(1, pageSize, search, selectedLookingFor, selectedPropertyType);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedLookingFor, selectedPropertyType, pageSize]);

  // Search -> debounce, reload current page
  useEffect(() => {
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    searchDebounceRef.current = setTimeout(() => {
      runLoad(1, pageSize, search, selectedLookingFor, selectedPropertyType);
    }, 300);
    return () => clearTimeout(searchDebounceRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  const handleSearch = (e) => {
    setSearch(e.target.value);
  };

  const handleLookingForChange = (event, newValue) => {
    setSelectedLookingFor(newValue);
    setSelectedPropertyType(''); // reset property type when looking for changes
  };

  const handlePropertyTypeChange = (event, newValue) => {
    setSelectedPropertyType(newValue);
  };

  const handlePageChange = (event, value) => {
    runLoad(value, pageSize, search, selectedLookingFor, selectedPropertyType);
  };

  const handlePageSizeChange = (event) => {
    setPageSize(parseInt(event.target.value, 10));
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this request?")) return;

    try {
      await axios.delete(`${BASE_URL}/property-request/${id}/`);
      toast.success("Deleted successfully!");
      runLoad(currentPage, pageSize, search, selectedLookingFor, selectedPropertyType);
    } catch (error) {
      console.error("Delete error:", error);
      toast.error("Failed to delete.");
    }
  };

  const handleView = (item) => {
    setSelectedRow(item);
    setOpenModal(true);
  };

  return (
    <Container maxWidth={false} sx={{ mt: 4 }}>
      <AdminDashboard />

      <Box
        sx={{
          maxWidth: 1400,
          mx: "auto",
          p: 2,
          borderRadius: 2,
          boxShadow: 3,
          backgroundColor: "#fff",
        }}
      >
        <Typography
          variant="h4"
          sx={{ textAlign: "center", color: "primary.main", mb: 3 }}
        >
          Property Requests
        </Typography>

        <Box sx={{ display: "flex", gap: 2, mb: 2, justifyContent: "space-between", alignItems: "center" }}>
          <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
            <TextField
              label="Search Location / Property Type / Looking For"
              size="small"
              value={search}
              onChange={handleSearch}
              sx={{ width: 300 }}
            />

            <FormControl size="small" sx={{ minWidth: 120 }}>
              <InputLabel>Records</InputLabel>
              <Select
                value={pageSize}
                label="Records"
                onChange={handlePageSizeChange}
              >
                <MenuItem value={10}>10</MenuItem>
                <MenuItem value={20}>20</MenuItem>
                <MenuItem value={30}>30</MenuItem>
                <MenuItem value={40}>40</MenuItem>
                <MenuItem value={50}>50</MenuItem>
                <MenuItem value={100}>100</MenuItem>
              </Select>
            </FormControl>

            {progressiveLoading && (
              <CircularProgress size={20} />
            )}
          </Box>
        </Box>

        {/* Looking For Tabs */}
        <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
          <Tabs
            value={selectedLookingFor}
            onChange={handleLookingForChange}
            variant="scrollable"
            scrollButtons="auto"
            aria-label="looking for tabs"
            sx={{
              '& .MuiTab-root': {
                textTransform: 'none',
                minWidth: 'auto',
                fontSize: '0.875rem',
                fontWeight: 500,
                '&.Mui-selected': {
                  color: '#1976d2',
                }
              },
              '& .MuiTabs-indicator': {
                backgroundColor: '#1976d2',
              }
            }}
          >
            <Tab 
              label="All Types" 
              value="" 
              sx={{ 
                '&.Mui-selected': {
                  color: '#1976d2',
                }
              }}
            />
            {Object.keys(PROPERTY_TYPE_OPTIONS).map((type) => (
              <Tab 
                key={type} 
                label={type} 
                value={type.toLowerCase()}
                sx={{ 
                  '&.Mui-selected': {
                    color: '#1976d2',
                  }
                }}
              />
            ))}
          </Tabs>
        </Box>

        {/* Property Type Tabs (conditional) */}
        {selectedLookingFor && PROPERTY_TYPE_OPTIONS[
          Object.keys(PROPERTY_TYPE_OPTIONS).find(
            (key) => key.toLowerCase() === selectedLookingFor
          )
        ]?.length > 0 && (
          <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
            <Tabs
              value={selectedPropertyType}
              onChange={handlePropertyTypeChange}
              variant="scrollable"
              scrollButtons="auto"
              aria-label="property type tabs"
              sx={{
                '& .MuiTab-root': {
                  textTransform: 'none',
                  minWidth: 'auto',
                  fontSize: '0.875rem',
                  fontWeight: 500,
                  '&.Mui-selected': {
                    color: '#1976d2',
                  }
                },
                '& .MuiTabs-indicator': {
                  backgroundColor: '#1976d2',
                }
              }}
            >
              <Tab 
                label="All Property Types" 
                value="" 
                sx={{ 
                  '&.Mui-selected': {
                    color: '#1976d2',
                  }
                }}
              />
              {PROPERTY_TYPE_OPTIONS[
                Object.keys(PROPERTY_TYPE_OPTIONS).find(
                  (key) => key.toLowerCase() === selectedLookingFor
                )
              ]?.map((type) => (
                <Tab 
                  key={type} 
                  label={type} 
                  value={type.toLowerCase()}
                  sx={{ 
                    '&.Mui-selected': {
                      color: '#1976d2',
                    }
                  }}
                />
              ))}
            </Tabs>
          </Box>
        )}

        {isLoading && requests.length === 0 ? (
          <Box display="flex" justifyContent="center" alignItems="center" sx={{ height: 300 }}>
            <CircularProgress />
          </Box>
        ) : (
          <TableContainer component={Paper} sx={{ maxHeight: 500 }}>
            <Table stickyHeader>
              <TableHead>
                <TableRow>
                  {[
                    "ID",
                    "Request ID",
                    "User Name",
                    "Mobile",
                    "Looking For",
                    "Property Type",
                    "Location",
                    "Budget Range",
                    "Created Date",
                    "Actions",
                  ].map((head) => (
                    <TableCell
                      key={head}
                      sx={{
                        backgroundColor: "#1976d2",
                        color: "white",
                        textAlign: "center",
                        fontWeight: "bold",
                        position: 'sticky',
                        top: 0,
                        zIndex: 1,
                      }}
                    >
                      {head}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>

              <TableBody>
                {requests.map((item, index) => (
                  <TableRow key={item.id}>
                    <TableCell align="center">{(currentPage - 1) * pageSize + index + 1}</TableCell>
                    <TableCell align="center">{item.req_id}</TableCell>
                    <TableCell align="center">{item.user_name}</TableCell>
                    <TableCell align="center">{item.user_mobile}</TableCell>
                    <TableCell align="center">{item.looking_for}</TableCell>
                    <TableCell align="center">{item.property_type}</TableCell>
                    <TableCell align="left" style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                      {item.locations && item.locations.length > 0
                        ? item.locations
                            .map((loc) => loc.location)
                            .filter(Boolean)
                            .join('\n')
                        : item.location}
                    </TableCell>
                    <TableCell align="center">
                      ₹{item.min_budget?.toLocaleString()} - ₹
                      {item.max_budget?.toLocaleString()}
                    </TableCell>
                    <TableCell align="center">
                      {new Date(item.created_at).toLocaleString()}
                    </TableCell>
                    <TableCell align="center">
                      <Stack direction="row" spacing={1} justifyContent="center">
                        <IconButton
                          color="info"
                          onClick={() => handleView(item)}
                        >
                          <VisibilityIcon />
                        </IconButton>
                        <IconButton
                          color="error"
                          onClick={() => handleDelete(item.id)}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        {/* PAGINATION CONTROLS */}        
        {hasLoadedOnce && (
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 2 }}>
            <Typography variant="body2" color="text.secondary">
              Showing {requests.length} of {totalCount} records
            </Typography>
            
            {totalPages > 1 && (
              <Pagination
                count={totalPages}
                page={currentPage}
                onChange={handlePageChange}
                color="primary"
                showFirstButton
                showLastButton
                siblingCount={1}
                boundaryCount={1}
                disabled={isLoading}
              />
            )}
          </Box>
        )}

        {/* MODAL */}
        <PropertyRequestModal
          open={openModal}
          onClose={() => setOpenModal(false)}
          data={selectedRow}
        />
      </Box>
          <ToastContainer />
    </Container>
  );
};

export default PropertyRequests;